import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

export default class extends Controller {

    static targets = [
        "log",
        "runButton",
        "closeButton",
        "reloadButton"
    ]

    static values = {
        runPath: String,
        opsType: String,
        recordId: String,
        reloadAfter: Boolean
    }

    connect() {
        this.lines = []
        this.progressItems = {}

        if (this.logTarget.value) {
            this.lines = this.logTarget.value.split("\n")
        }

        this.operationId = this.makeOperationId()

        this.modalElement = this.element.closest(".modal")

        if (this.modalElement) {
            this.modalElement.dataset.keepOpen = "true"
        }

        this.createProgressContainer()
        this.subscribe()
    }

    disconnect() {
        this.unsubscribe()

        if (this.modalElement) {
            delete this.modalElement.dataset.keepOpen
        }

        if (this.progressContainer) {
            this.progressContainer.remove()
            this.progressContainer = null
        }
    }

    createProgressContainer() {
        if (this.progressContainer) return

        const container = document.createElement("div")

        container.className = "mt-3"

        this.logTarget.insertAdjacentElement(
            "afterend",
            container
        )

        this.progressContainer = container
    }

    makeOperationId() {
        let sessionId =
            sessionStorage.getItem(
                "long_operation_session_id"
            )

        if (!sessionId) {
            sessionId = crypto.randomUUID()

            sessionStorage.setItem(
                "long_operation_session_id",
                sessionId
            )
        }

        const taskId = crypto.randomUUID()

        return `${sessionId}:${taskId}`
    }

    subscribe() {
        this.channel =
            consumer.subscriptions.create(
                {
                    channel: "LongOperationsChannel",
                    operation_id: this.operationId
                },
                {
                    received: (data) => {
                        this.received(data)
                    }
                }
            )
    }

    unsubscribe() {
        if (!this.channel) return

        consumer.subscriptions.remove(
            this.channel
        )

        this.channel = null
    }

    received(data) {
        if (
            data.operation_id &&
            data.operation_id !== this.operationId
        ) {
            return
        }

        switch (data.type) {

            case "log":
                this.appendLog(
                    data.message
                )

                break

            case "progress":
                this.updateProgress(
                    data.key,
                    data.message,
                    data.current,
                    data.total
                )

                break

            case "finished":
                this.completeProgress()

                this.appendLog(
                    data.message
                )

                this.runButtonTarget.disabled = true
                this.closeButtonTarget.disabled = false

                if (
                    this.reloadAfterValue &&
                    this.hasReloadButtonTarget
                ) {
                    this.reloadButtonTarget.disabled = false
                }

                break
        }
    }

    appendLog(message) {
        this.lines.push(message)

        this.renderLog()
    }

    updateProgress(key, message, current, total) {
        this.progressItems[key] = {
            message,
            current,
            total
        }

        this.renderProgress()
    }

    completeProgress() {
        Object.values(
            this.progressItems
        ).forEach(item => {
            if (
                Number(item.total) > 0 &&
                Number(item.current) < Number(item.total)
            ) {
                item.current = item.total
            }
        })

        this.renderProgress()
    }

    renderProgress() {
        if (!this.progressContainer) {
            this.createProgressContainer()
        }

        const items =
            Object.entries(
                this.progressItems
            )

        this.progressContainer.innerHTML = ""

        if (items.length === 0) {
            return
        }

        items.forEach(([key, item]) => {
            const current =
                Number(item.current) || 0

            const total =
                Number(item.total) || 0

            const percent =
                total > 0
                    ? Math.min(
                        100,
                        Math.max(
                            0,
                            Math.round(
                                current / total * 100
                            )
                        )
                    )
                    : 0

            const wrapper =
                document.createElement("div")

            wrapper.className = "mb-3"
            wrapper.dataset.progressKey = key

            const header =
                document.createElement("div")

            header.className =
                "d-flex justify-content-between align-items-center mb-1 gap-3"

            const message =
                document.createElement("span")

            message.className =
                "small text-body-secondary"

            message.textContent =
                item.message || ""

            const counter =
                document.createElement("span")

            counter.className =
                "small text-body-secondary text-nowrap"

            counter.textContent =
                `${current}/${total}`

            header.append(
                message,
                counter
            )

            const progress =
                document.createElement("div")

            progress.className =
                "progress position-relative"

            progress.style.height = "28px"

            progress.setAttribute(
                "role",
                "progressbar"
            )

            progress.setAttribute(
                "aria-valuenow",
                percent
            )

            progress.setAttribute(
                "aria-valuemin",
                "0"
            )

            progress.setAttribute(
                "aria-valuemax",
                "100"
            )

            const bar =
                document.createElement("div")

            bar.className = "progress-bar"
            bar.style.width = `${percent}%`

            const percentText =
                document.createElement("div")

            percentText.className =
                "position-absolute top-50 start-50 translate-middle fw-semibold"

            percentText.style.zIndex = "2"
            percentText.style.pointerEvents = "none"

            percentText.textContent =
                `${percent}%`

            progress.append(
                bar,
                percentText
            )

            wrapper.append(
                header,
                progress
            )

            this.progressContainer.append(
                wrapper
            )
        })
    }

    renderLog() {
        this.logTarget.value =
            this.lines.join("\n")

        this.logTarget.scrollTop =
            this.logTarget.scrollHeight
    }

    async run() {
        this.runButtonTarget.disabled = true
        this.closeButtonTarget.disabled = true

        if (this.hasReloadButtonTarget) {
            this.reloadButtonTarget.disabled = true
        }

        this.progressItems = {}
        this.renderProgress()

        this.appendLog(
            "Запуск операции..."
        )

        const body =
            new FormData()

        body.append(
            "ops_type",
            this.opsTypeValue
        )

        body.append(
            "operation_id",
            this.operationId
        )

        if (
            this.hasRecordIdValue &&
            this.recordIdValue
        ) {
            body.append(
                "id",
                this.recordIdValue
            )
        }

        try {
            const response =
                await fetch(
                    this.runPathValue,
                    {
                        method: "PUT",
                        body: body,
                        headers: {
                            "X-CSRF-Token":
                            document.querySelector(
                                'meta[name="csrf-token"]'
                            ).content,
                            "Accept": "application/json"
                        }
                    }
                )

            if (!response.ok) {
                this.appendLog(
                    "Ошибка запуска операции"
                )

                this.runButtonTarget.disabled = false
                this.closeButtonTarget.disabled = false

                if (
                    this.reloadAfterValue &&
                    this.hasReloadButtonTarget
                ) {
                    this.reloadButtonTarget.disabled = false
                }
            }

        } catch (error) {
            console.error(error)

            this.appendLog(
                "Ошибка запуска операции"
            )

            this.runButtonTarget.disabled = false
            this.closeButtonTarget.disabled = false

            if (
                this.reloadAfterValue &&
                this.hasReloadButtonTarget
            ) {
                this.reloadButtonTarget.disabled = false
            }
        }
    }

    closeAndReload() {
        const modalElement =
            this.element.closest(".modal")

        if (!modalElement) {
            Turbo.visit(
                window.location.href,
                {
                    action: "replace"
                }
            )

            return
        }

        modalElement.addEventListener(
            "hidden.bs.modal",
            () => {
                Turbo.visit(
                    window.location.href,
                    {
                        action: "replace"
                    }
                )
            },
            {
                once: true
            }
        )

        const modal =
            bootstrap.Modal.getInstance(
                modalElement
            )

        if (modal) {
            modal.hide()
        } else {
            Turbo.visit(
                window.location.href,
                {
                    action: "replace"
                }
            )
        }
    }
}